import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import type { LocalProfile, UserRole } from "./auth-types";
import {
  AUTH_STORAGE_SYNC_KEYS,
  clearAuthStorage,
  clearImpersonationSession,
  loadAppJwt,
  loadGoogleIdToken,
  loadImpersonationMeta,
  loadImpersonationResumeJwt,
  loadProfile,
  migrateAuthStorageFromSession,
  savePkceBundle,
  saveProfile,
  saveGoogleTokens,
  saveAppJwt,
  saveImpersonationMeta,
  saveImpersonationResumeJwt,
  type ImpersonationMeta,
} from "./auth-session";
import {
  patchUsersMe,
  getAuthMePermissions,
  postAdminImpersonateUser,
  postAdminImpersonationEnd,
} from "./api-client";
import { clearPostLoginReturnTo } from "./post-login-redirect";
import { clearSignupIntent } from "./signup-intent";
import { createPkcePair, buildGoogleAuthorizeUrl } from "./google-oauth-pkce";
import {
  decodeJwtPayload,
  decodeAppJwtClaims,
  isJwtExpired,
} from "./jwt-payload";
import { isApiConfigured } from "../auth-config";

export interface GoogleIdentity {
  sub: string;
  email: string;
  name?: string;
}

type AuthStatus = "anonymous" | "authenticated";

interface AuthState {
  status: AuthStatus;
  bearerToken: string | null;
  identity: GoogleIdentity | null;
  profile: LocalProfile | null;
  impersonation: ImpersonationMeta | null;
}

const listeners = new Set<() => void>();

let cachedSnapshot: AuthState | null = null;
let cachedSnapshotKey = "";

function snapshotCacheKey(s: AuthState): string {
  if (s.status === "anonymous") return "anon";
  return [
    s.bearerToken ?? "",
    s.profile?.role,
    s.profile?.googleSub,
    s.profile?.email,
    s.profile?.enrollmentSummary,
    s.identity?.name ?? "",
    s.impersonation?.targetUserId ?? "",
    s.impersonation?.impersonatorSub ?? "",
  ].join("|");
}

function emit() {
  cachedSnapshot = null;
  cachedSnapshotKey = "";
  for (const fn of listeners) fn();
}

function computeGoogleFallbackState(googleId: string): AuthState {
  const payload = decodeJwtPayload(googleId);
  const sub = typeof payload?.sub === "string" ? payload.sub : "";
  const email = typeof payload?.email === "string" ? payload.email : "";
  const name = typeof payload?.name === "string" ? payload.name : undefined;

  if (!sub) {
    return {
      status: "anonymous",
      bearerToken: null,
      identity: null,
      profile: null,
      impersonation: null,
    };
  }

  const stored = loadProfile();
  const profile: LocalProfile | null =
    stored && stored.googleSub === sub
      ? stored
      : {
          role: "pending",
          googleSub: sub,
          email,
          displayName: name,
        };

  return {
    status: "authenticated",
    bearerToken: googleId,
    identity: { sub, email, name },
    profile,
    impersonation: null,
  };
}

/** Pure read: no auth storage writes (safe for React getSnapshot). */
function computeAuthState(): AuthState {
  const appJwt = loadAppJwt();
  if (appJwt && !isJwtExpired(appJwt)) {
    const claims = decodeAppJwtClaims(appJwt);
    if (claims) {
      const stored = loadProfile();
      const profile: LocalProfile = {
        role: claims.role,
        googleSub: claims.sub,
        email: claims.email,
        displayName: stored?.googleSub === claims.sub ? stored.displayName : undefined,
        enrollmentSummary: claims.enrollmentSummary ?? stored?.enrollmentSummary,
      };
      const meta = loadImpersonationMeta();
      let impersonation: ImpersonationMeta | null = null;
      if (claims.impersonation && claims.impersonatorSub) {
        const targetRole: "teacher" | "student" =
          claims.role === "student" ? "student" : "teacher";
        impersonation =
          meta && meta.targetGoogleSub === claims.sub
            ? meta
            : {
                targetUserId: meta?.targetUserId ?? "",
                targetEmail: claims.email,
                targetRole,
                targetGoogleSub: claims.sub,
                impersonatorSub: claims.impersonatorSub,
              };
      }
      return {
        status: "authenticated",
        bearerToken: appJwt,
        identity: {
          sub: claims.sub,
          email: claims.email,
          name: profile.displayName,
        },
        profile,
        impersonation,
      };
    }
  }

  const googleId = loadGoogleIdToken();
  if (!googleId || isJwtExpired(googleId)) {
    return {
      status: "anonymous",
      bearerToken: null,
      identity: null,
      profile: null,
      impersonation: null,
    };
  }

  return computeGoogleFallbackState(googleId);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): AuthState {
  const next = computeAuthState();
  const key = snapshotCacheKey(next);
  if (cachedSnapshot && key === cachedSnapshotKey) return cachedSnapshot;
  cachedSnapshot = next;
  cachedSnapshotKey = key;
  return next;
}

function getServerSnapshot(): AuthState {
  return {
    status: "anonymous",
    bearerToken: null,
    identity: null,
    profile: null,
    impersonation: null,
  };
}

interface AuthContextValue {
  status: AuthStatus;
  identity: GoogleIdentity | null;
  profile: LocalProfile | null;
  isAdmin: boolean;
  adminResolved: boolean;
  /** Present when the active app JWT is an admin impersonation session. */
  impersonation: ImpersonationMeta | null;
  /**
   * Prefer app JWT for `Authorization: Bearer` when the API is deployed.
   * Falls back to the Google ID token only when `VITE_API_BASE_URL` is unset (offline dev).
   */
  getBearerToken: () => string | null;
  beginGoogleSignIn: () => Promise<void>;
  signOut: () => void;
  setRole: (role: Exclude<UserRole, "pending">) => Promise<void>;
  setEnrollmentSummary: (summary: LocalProfile["enrollmentSummary"]) => Promise<void>;
  startImpersonation: (userId: string, reason?: string) => Promise<"teacher" | "student">;
  endImpersonation: () => Promise<void>;
  refresh: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminResolved, setAdminResolved] = useState(false);

  useEffect(() => {
    if (state.status !== "authenticated" || !state.bearerToken) {
      setIsAdmin(false);
      setAdminResolved(state.status === "anonymous");
      return;
    }

    if (!isApiConfigured()) {
      setIsAdmin(false);
      setAdminResolved(true);
      return;
    }

    let cancelled = false;
    setAdminResolved(false);
    void getAuthMePermissions(state.bearerToken)
      .then((data) => {
        if (!cancelled) {
          setIsAdmin(data.isAdmin === true);
          setAdminResolved(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsAdmin(false);
          setAdminResolved(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [state.status, state.bearerToken]);

  useEffect(() => {
    migrateAuthStorageFromSession();
    const app = loadAppJwt();
    const g = loadGoogleIdToken();
    const appMissingOrDead = !app || isJwtExpired(app);
    const gMissingOrDead = !g || isJwtExpired(g);
    if (appMissingOrDead && gMissingOrDead && (app !== null || g !== null)) {
      clearAuthStorage();
      emit();
    }
  }, []);

  useEffect(() => {
    function onStorage(ev: StorageEvent) {
      if (ev.storageArea !== localStorage) return;
      if (!ev.key || !AUTH_STORAGE_SYNC_KEYS.includes(ev.key)) return;
      emit();
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const refresh = useCallback(() => emit(), []);

  const beginGoogleSignIn = useCallback(async () => {
    const { verifier, challenge } = await createPkcePair();
    const stateToken = crypto.randomUUID();
    savePkceBundle({ codeVerifier: verifier, state: stateToken });
    window.location.href = buildGoogleAuthorizeUrl({
      codeChallenge: challenge,
      state: stateToken,
    });
  }, []);

  const signOut = useCallback(() => {
    clearPostLoginReturnTo();
    clearAuthStorage();
    emit();
  }, []);

  const startImpersonation = useCallback(async (userId: string, reason?: string) => {
    const adminJwt = loadAppJwt();
    if (!adminJwt || isJwtExpired(adminJwt)) {
      throw new Error("Admin session required to impersonate");
    }
    if (loadImpersonationMeta() || decodeAppJwtClaims(adminJwt)?.impersonation) {
      throw new Error("Already impersonating another user");
    }
    const out = await postAdminImpersonateUser(adminJwt, userId, reason ? { reason } : undefined);
    if (out.user.role !== "teacher" && out.user.role !== "student") {
      throw new Error("Unexpected impersonation target role");
    }
    saveImpersonationResumeJwt(adminJwt);
    saveImpersonationMeta({
      targetUserId: out.user.id,
      targetEmail: out.user.email,
      targetRole: out.user.role,
      targetGoogleSub: out.user.googleSub,
      impersonatorSub: out.impersonation.impersonatorSub,
    });
    saveAppJwt(out.accessToken);
    saveProfile({
      googleSub: out.user.googleSub,
      email: out.user.email,
      role: out.user.role,
      enrollmentSummary:
        out.user.enrollmentSummary === "active" ||
        out.user.enrollmentSummary === "none" ||
        out.user.enrollmentSummary === "archived_only"
          ? out.user.enrollmentSummary
          : undefined,
    });
    emit();
    return out.user.role;
  }, []);

  const endImpersonation = useCallback(async () => {
    const meta = loadImpersonationMeta();
    const resume = loadImpersonationResumeJwt();
    if (!resume || isJwtExpired(resume)) {
      clearImpersonationSession();
      clearAuthStorage();
      emit();
      throw new Error("Admin session expired. Sign in again.");
    }

    try {
      await postAdminImpersonationEnd(resume, {
        targetUserId: meta?.targetUserId,
        targetGoogleSub: meta?.targetGoogleSub,
      });
    } catch {
      /* best-effort audit */
    }

    const claims = decodeAppJwtClaims(resume);
    clearImpersonationSession();
    saveAppJwt(resume);
    if (claims) {
      const prev = loadProfile();
      saveProfile({
        googleSub: claims.sub,
        email: claims.email,
        role: claims.role,
        displayName: prev?.googleSub === claims.sub ? prev.displayName : undefined,
        enrollmentSummary: claims.enrollmentSummary ?? prev?.enrollmentSummary,
      });
    }
    emit();
  }, []);

  const setRole = useCallback(async (role: Exclude<UserRole, "pending">) => {
    const useApi = isApiConfigured();
    const bearer = loadAppJwt();
    if (useApi && bearer && !isJwtExpired(bearer)) {
      const out = await patchUsersMe(bearer, { role });
      saveAppJwt(out.accessToken);
      const prev = loadProfile();
      saveProfile({
        googleSub: out.user.googleSub,
        email: out.user.email,
        role:
          out.user.role === "teacher" || out.user.role === "student" || out.user.role === "pending"
            ? out.user.role
            : role,
        displayName: prev?.googleSub === out.user.googleSub ? prev.displayName : undefined,
        enrollmentSummary:
          out.user.enrollmentSummary === "active" ||
          out.user.enrollmentSummary === "none" ||
          out.user.enrollmentSummary === "archived_only"
            ? out.user.enrollmentSummary
            : prev?.enrollmentSummary,
      });
      clearSignupIntent();
      emit();
      return;
    }

    const googleId = loadGoogleIdToken();
    if (!googleId || isJwtExpired(googleId)) return;
    const payload = decodeJwtPayload(googleId);
    const sub = typeof payload?.sub === "string" ? payload.sub : "";
    const email = typeof payload?.email === "string" ? payload.email : "";
    const name = typeof payload?.name === "string" ? payload.name : undefined;
    if (!sub) return;

    const prev = loadProfile();
    const base: LocalProfile =
      prev && prev.googleSub === sub
        ? prev
        : { role: "pending", googleSub: sub, email, displayName: name };

    saveProfile({ ...base, googleSub: sub, email: email || base.email, role });
    clearSignupIntent();
    emit();
  }, []);

  const setEnrollmentSummary = useCallback(
    async (summary: LocalProfile["enrollmentSummary"]) => {
      const useApi = isApiConfigured();
      const bearer = loadAppJwt();
      if (
        useApi &&
        bearer &&
        !isJwtExpired(bearer) &&
        (summary === "active" || summary === "none" || summary === "archived_only")
      ) {
        const out = await patchUsersMe(bearer, { enrollmentSummary: summary });
        saveAppJwt(out.accessToken);
        const prev = loadProfile();
        saveProfile({
          googleSub: out.user.googleSub,
          email: out.user.email,
          role:
            out.user.role === "teacher" || out.user.role === "student" || out.user.role === "pending"
              ? out.user.role
              : prev?.role ?? "pending",
          displayName: prev?.googleSub === out.user.googleSub ? prev.displayName : undefined,
          enrollmentSummary:
            out.user.enrollmentSummary === "active" ||
            out.user.enrollmentSummary === "none" ||
            out.user.enrollmentSummary === "archived_only"
              ? out.user.enrollmentSummary
              : summary,
        });
        emit();
        return;
      }

      const p = loadProfile();
      if (!p) return;
      saveProfile({ ...p, enrollmentSummary: summary });
      emit();
    },
    []
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status: state.status,
      identity: state.identity,
      profile: state.profile,
      isAdmin,
      adminResolved,
      impersonation: state.impersonation,
      getBearerToken: () => {
        const app = loadAppJwt();
        if (app && !isJwtExpired(app)) return app;
        if (isApiConfigured()) return null;
        const g = loadGoogleIdToken();
        if (g && !isJwtExpired(g)) return g;
        return null;
      },
      beginGoogleSignIn,
      signOut,
      setRole,
      setEnrollmentSummary,
      startImpersonation,
      endImpersonation,
      refresh,
    }),
    [
      state.status,
      state.identity,
      state.profile,
      isAdmin,
      adminResolved,
      state.impersonation,
      beginGoogleSignIn,
      signOut,
      setRole,
      setEnrollmentSummary,
      startImpersonation,
      endImpersonation,
      refresh,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** After Google OAuth redirect: persist Google tokens only. */
export function commitGoogleSession(idToken: string, accessToken?: string): void {
  saveGoogleTokens(idToken, accessToken);
  emit();
}
